import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ServiceCard from "@/components/ServiceCard";
import { GENDERS } from "@/lib/services";
import { Search, Plus, ShieldCheck, MessageSquare, Phone, X } from "lucide-react";

const chip = (on) => `rounded-full px-4 py-1.5 text-sm border transition-colors ${on ? "bg-rose-600 border-rose-600 text-white" : "border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:text-zinc-100"}`;

export default function Services() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const [category, setCategory] = useState(params.get("categorie") || "");
  const [gender, setGender] = useState("");
  const [location, setLocation] = useState("");
  const [todayOnly, setTodayOnly] = useState(params.get("vandaag") === "1");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [debouncedQ, setDebouncedQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    const params = { type: "service" };
    if (debouncedQ) params.q = debouncedQ;
    if (category) params.category = category;
    try {
      const { data } = await api.get("/products", { params });
      setItems(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, category]);

  useEffect(() => { api.get("/service-categories").then(({ data }) => setCategories(data)); }, []);
  useEffect(() => { if (user) api.get("/favorites/ids").then(({ data }) => setFavIds(data)).catch(() => {}); }, [user]);
  useEffect(() => { load(); }, [load]);

  const toggleFav = async (id) => {
    const { data } = await api.post(`/favorites/${id}`);
    setFavIds((prev) => (data.favorited ? [...prev, id] : prev.filter((x) => x !== id)));
  };

  const shown = items.filter((s) =>
    (!todayOnly || s.available_today) &&
    (!gender || s.gender === gender) &&
    (!location || (s.location || "").toLowerCase().includes(location.toLowerCase())));

  return (
    <div>
      <section className="relative overflow-hidden border-b border-zinc-800/60">
        <div className="absolute -top-20 right-0 w-[32rem] h-[32rem] glow-rose blur-3xl opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
          <span className="font-mono text-[11px] uppercase tracking-widest text-rose-300">Diensten · 18+</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-zinc-100 max-w-3xl">
            Discrete <span className="italic text-rose-400">diensten</span> bij jou in de buurt
          </h1>
          <p className="mt-5 text-base text-zinc-400 max-w-xl">Escort, massage, SM & fetish en meer — van volwassen aanbieders. Neem contact op via de chat of bel direct.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
              <input data-testid="service-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek op naam of omschrijving..."
                className="w-full rounded-full border border-zinc-800 bg-zinc-900/80 pl-12 pr-4 py-3.5 text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none" />
            </div>
            <Link to={user ? "/dienst-plaatsen" : "/auth"} data-testid="services-create-button"
              className="flex items-center justify-center gap-2 rounded-full bg-rose-600 hover:bg-rose-500 text-white font-medium px-6 py-3.5 transition-transform hover:scale-[1.02]">
              <Plus className="h-4 w-4" /> Bied je dienst aan — gratis
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-6 text-xs text-zinc-500">
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-rose-400" /> Alleen 18+ aanbieders</span>
            <span className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-rose-400" /> Anonieme chat</span>
            <span className="flex items-center gap-2"><Phone className="h-4 w-4 text-rose-400" /> Nummer zichtbaar na inloggen</span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-12">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button data-testid="service-category-all" onClick={() => setCategory("")} className={chip(category === "")}>Alle diensten</button>
          {categories.map((c) => (
            <button key={c} data-testid={`service-category-${c}`} onClick={() => setCategory(c)} className={chip(category === c)}>{c}</button>
          ))}
        </div>
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <select data-testid="service-filter-gender" value={gender} onChange={(e) => setGender(e.target.value)} className="rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200">
            <option value="">Alle geslachten</option>
            {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <input data-testid="service-filter-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Plaats, bijv. Utrecht"
            className="w-48 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-200 focus:border-rose-700 focus:outline-none" />
          <button data-testid="service-filter-today" aria-pressed={todayOnly} onClick={() => setTodayOnly((v) => !v)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${todayOnly ? "border-emerald-500 bg-emerald-600 text-white" : "border-emerald-800/60 bg-emerald-950/20 text-emerald-300 hover:bg-emerald-950/50"}`}>
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
            Vandaag beschikbaar
          </button>
          {(gender || location || category || q || todayOnly) && (
            <button data-testid="service-clear-filters" onClick={() => { setGender(""); setLocation(""); setCategory(""); setQ(""); setTodayOnly(false); }} className="flex items-center gap-1 text-sm text-rose-400 hover:text-rose-300">
              <X className="h-3.5 w-3.5" /> Wis filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-xl bg-zinc-900/60 animate-pulse" />)}
          </div>
        ) : error ? (
          <div data-testid="services-error-state" className="text-center py-24">
            <p className="font-serif text-2xl text-zinc-300">Diensten konden niet worden geladen</p>
            <button data-testid="services-retry-button" onClick={load} className="mt-4 rounded-full bg-rose-600 hover:bg-rose-500 px-5 py-2 text-sm text-white">Opnieuw proberen</button>
          </div>
        ) : shown.length === 0 ? (
          <div data-testid="services-empty-state" className="text-center py-24">
            <p className="font-serif text-2xl text-zinc-300">Nog geen diensten gevonden</p>
            <p className="text-sm text-zinc-500 mt-2">Wees de eerste — plaatsen is nu gratis.</p>
          </div>
        ) : (
          <div data-testid="service-grid" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
            {shown.map((s) => <ServiceCard key={s.id} product={s} favorited={favIds.includes(s.id)} onToggleFav={user ? toggleFav : null} />)}
          </div>
        )}
      </section>
    </div>
  );
}
